//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import angular from 'angular';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ModerationStatus } from '@apis/moderation/moderationApiModel';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { BulkRejectConfirmationModal } from '@media-library/ai-content-curation/components/BulkRejectConfirmationModal';
import { AiContentCurationTrackingIds } from '@media-library/ai-content-curation/constants/AiContentCurationTrackingIds';
import { useUpdateModerationStatusMutation } from '@media-library/queries';
import Cuid from '@models/Cuid';
import { Permissions } from '@models/Permissions';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { getClientId } from '@redux/selectors/getClientId';

import { getBulkActionsSelectedItems } from 'app/bulk-actions/selectors/bulk-actions-selector';

export const BulkActionsBulkReject = () => {
  const dispatch = useDispatch();
  const clientId = useSelector(getClientId);
  const selectedItems = useSelector(getBulkActionsSelectedItems);

  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false);

  const { isPending: isRejecting, mutate: updateStatus } =
    useUpdateModerationStatusMutation();

  const onConfirmReject = () => {
    setIsBulkRejectModalOpen(false);
    updateStatus(
      {
        cuids: selectedItems.map((item) =>
          Cuid.fromPartial(item.cuid, clientId),
        ),
        status: ModerationStatus.Rejected,
      },
      {
        onSuccess: () => dispatch(getBulkActionsActions().clearSelectedItems()),
      },
    );
  };

  return (
    <>
      <RejectButton
        iconName='close'
        isDisabled={selectedItems.length === 0 || isRejecting}
        onClick={() => setIsBulkRejectModalOpen(true)}
        permissionName={Permissions.FanreelModerate}
        trackingId={AiContentCurationTrackingIds.BULK_REJECT_BUTTON}
      >
        Reject
      </RejectButton>
      <BulkRejectConfirmationModal
        isOpen={isBulkRejectModalOpen}
        itemCount={selectedItems.length}
        onCancel={() => setIsBulkRejectModalOpen(false)}
        onConfirm={onConfirmReject}
      />
    </>
  );
};

const RejectButton = styled(RequiredPermissionsButton).attrs({
  buttonStyles: {
    fixed: {
      fontWeight: 'bold',
      borderShape: 'pill',
      borderThickness: 'thick',
    },
    standard: {
      color: theme.colors.white,
      backgroundColor: theme.colors.aliases.primary,
      borderColor: theme.colors.white,
    },
    hover: {
      color: theme.colors.aliases.primary,
      backgroundColor: theme.colors.white,
      borderColor: theme.colors.white,
    },
    active: {
      color: theme.colors.aliases.primary,
      backgroundColor: theme.colors.white,
      borderColor: theme.colors.white,
    },
    disabled: {
      color: theme.colors.gray4,
      backgroundColor: theme.colors.gray0,
      borderColor: theme.colors.gray0,
    },
  },
})(
  ({ theme }) => `
  margin-right: ${theme.spacings[4]};
  /* need to set to exactly 37px to match existing buttons in bulk actions */
  height: 37px; 
  box-shadow: none;
  border: ${theme.borderWidths[2]} solid ${theme.colors.white};
`,
);

angular.module('curalate').component('bulkActionsBulkReject', {
  controller: makeAngularReactController(<BulkActionsBulkReject />),
});
